import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowCounterClockwise, GitBranch } from '@phosphor-icons/react';

import type {
  FlowProfile,
  NoticeConfigValues,
  ResolvedNoticeConfig,
} from '@/api/notices';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// NoticeConfigForm edits ONE stored layer of notices config (a team category's
// defaults or a team's override). Every field is field-level nullable: a null
// field inherits from the layer below (team → category → flow default). There
// are two presentation modes; the null-based cascade under the hood is identical
// in both — only the controls differ.
//
// TEAM mode (default, `inherited` supplied): each control shows the CONCRETE
// value the team currently resolves to — never the word "inherit". A field the
// team overrides (its value differs from what it would inherit from the
// category) is flagged with a badge and a "reset to category" link. Picking a
// value equal to the inherited baseline stores null (back to inheriting), so the
// badge only shows genuine divergences. This is what the team editor uses so an
// admin sees the real settings, plus a nudge wherever they've diverged.
//
// CATEGORY mode (`hideInherit`): the category has no team above it, so we drop
// the "inherit" affordance entirely and show the concrete flow-profile default.
// An untouched field still saves null and resolves to that same default.

const INHERIT = '__inherit__';

// FormMode lets the labelled helpers (TriState / NumberField / TextField) pick
// up the presentation mode without prop-drilling to every call site. teamMode is
// the per-field concrete-value + override-badge presentation; hideInherit is the
// category presentation. At most one is true.
interface FormMode {
  hideInherit: boolean;
  teamMode: boolean;
}
const ModeCtx = createContext<FormMode>({ hideInherit: false, teamMode: false });
const FLOWS: FlowProfile[] = [
  'regular_team',
  'booking',
  'open_tryout',
  'tryout_approval',
  'free_group',
  'manual',
];
const CURRENCIES = ['SEK', 'NOK', 'DKK', 'EUR'];

export interface NoticeConfigFormProps {
  values: NoticeConfigValues;
  onChange: (next: NoticeConfigValues) => void;
  // Effective values (all layers merged, INCLUDING the team's own overrides).
  resolved?: ResolvedNoticeConfig | undefined;
  // The baseline the team would resolve to with NO overrides of its own
  // (category defaults over flow defaults). Its presence switches the form into
  // team mode; each field is diffed against it to decide the override badge.
  inherited?: ResolvedNoticeConfig | undefined;
  disabled?: boolean;
  // Category mode: hide the "inherit" affordance and show concrete defaults.
  hideInherit?: boolean;
}

// OverrideMark renders the "differs from the category" badge + a reset link that
// clears the field back to inheriting. Rendered next to a field's label in team
// mode only, and only when the field is actually diverging.
function OverrideMark({ onReset, disabled }: { onReset: () => void; disabled?: boolean | undefined }) {
  const { t } = useTranslation('common');
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-pill border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <GitBranch size={12} weight="bold" aria-hidden />
        {t('notices.config.overridden')}
      </span>
      <button
        type="button"
        onClick={onReset}
        disabled={!!disabled}
        className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-medium text-foreground/70 underline-offset-2 hover:bg-muted hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        <ArrowCounterClockwise size={12} weight="bold" aria-hidden />
        {t('notices.config.resetToCategory')}
      </button>
    </span>
  );
}

// A labelled row wrapper. `labelExtra` renders inline after the label (the
// override badge + reset link in team mode).
function Field({
  label,
  help,
  labelExtra,
  children,
}: {
  label: string;
  help?: string;
  labelExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-foreground">{label}</span>
        {labelExtra}
      </span>
      {children}
      {help && <span className="text-xs font-normal text-muted-foreground">{help}</span>}
    </label>
  );
}

// TriState edits a nullable boolean. `inheritedHint` is the value the field
// falls back to: the flow default (category mode) or the category baseline
// (team mode).
function TriState({
  label,
  value,
  onChange,
  inheritedHint,
  disabled,
}: {
  label: string;
  value: boolean | null | undefined;
  onChange: (v: boolean | null) => void;
  inheritedHint?: boolean | undefined;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation('common');
  const { hideInherit, teamMode } = useContext(ModeCtx);

  if (hideInherit) {
    // Category mode: concrete Ja/Nej, defaulting to the flow-profile default.
    // Untouched → value stays null and still resolves to that default.
    const effective = value ?? inheritedHint ?? false;
    return (
      <Field label={label}>
        <Select
          value={effective ? 'yes' : 'no'}
          onValueChange={(v) => onChange(v === 'yes')}
          disabled={!!disabled}
        >
          <SelectTrigger className="min-h-[var(--tap)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">{t('common.yes')}</SelectItem>
            <SelectItem value="no">{t('common.no')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    );
  }

  if (teamMode) {
    // Team mode: show the concrete effective value; picking the category
    // baseline clears the override (stores null).
    const base = inheritedHint ?? false;
    const effective = value ?? base;
    const overridden = value != null && value !== base;
    return (
      <Field
        label={label}
        labelExtra={
          overridden ? <OverrideMark onReset={() => onChange(null)} disabled={disabled} /> : undefined
        }
      >
        <Select
          value={effective ? 'yes' : 'no'}
          onValueChange={(v) => {
            const picked = v === 'yes';
            onChange(picked === base ? null : picked);
          }}
          disabled={!!disabled}
        >
          <SelectTrigger className="min-h-[var(--tap)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">{t('common.yes')}</SelectItem>
            <SelectItem value="no">{t('common.no')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    );
  }

  const sel = value == null ? INHERIT : value ? 'yes' : 'no';
  const inheritLabel =
    inheritedHint === undefined
      ? t('notices.config.inherit')
      : t('notices.config.inheritValue', { value: t(inheritedHint ? 'common.yes' : 'common.no') });
  return (
    <Field label={label}>
      <Select
        value={sel}
        onValueChange={(v) => onChange(v === INHERIT ? null : v === 'yes')}
        disabled={!!disabled}
      >
        <SelectTrigger className="min-h-[var(--tap)]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
          <SelectItem value="yes">{t('common.yes')}</SelectItem>
          <SelectItem value="no">{t('common.no')}</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
}

// NumberField: empty input = inherit (null). In team mode the inherited number
// (if any) shows as the placeholder so the effective value stays visible.
function NumberField({
  label,
  value,
  onChange,
  min,
  inheritedHint,
  disabled,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  min?: number | undefined;
  inheritedHint?: number | undefined;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation('common');
  const { hideInherit, teamMode } = useContext(ModeCtx);
  const overridden = teamMode && value != null && value !== inheritedHint;
  const help = hideInherit
    ? t('notices.config.blankOptional')
    : teamMode && inheritedHint != null
      ? t('notices.config.inheritsValue', { value: inheritedHint })
      : t('notices.config.blankInherits');
  return (
    <Field
      label={label}
      help={help}
      labelExtra={
        overridden ? <OverrideMark onReset={() => onChange(null)} disabled={disabled} /> : undefined
      }
    >
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        className="rounded-pill"
        value={value ?? ''}
        placeholder={teamMode && inheritedHint != null ? String(inheritedHint) : undefined}
        onChange={(e) => {
          const raw = e.target.value.trim();
          onChange(raw === '' ? null : Math.max(min ?? 0, Number(raw) || 0));
        }}
        disabled={!!disabled}
      />
    </Field>
  );
}

// TextField: empty = inherit (null). In team mode the inherited text (if any)
// shows as the placeholder so the effective value stays visible.
function TextField({
  label,
  value,
  onChange,
  placeholder,
  inheritedHint,
  disabled,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  placeholder?: string | undefined;
  inheritedHint?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation('common');
  const { hideInherit, teamMode } = useContext(ModeCtx);
  const overridden = teamMode && value != null && value !== inheritedHint;
  const help = hideInherit
    ? t('notices.config.blankOptional')
    : teamMode && inheritedHint
      ? t('notices.config.inheritsValue', { value: inheritedHint })
      : t('notices.config.blankInherits');
  return (
    <Field
      label={label}
      help={help}
      labelExtra={
        overridden ? <OverrideMark onReset={() => onChange(null)} disabled={disabled} /> : undefined
      }
    >
      <Input
        type="text"
        value={value ?? ''}
        placeholder={teamMode && inheritedHint ? inheritedHint : placeholder}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={!!disabled}
      />
    </Field>
  );
}

export function NoticeConfigForm({
  values,
  onChange,
  resolved,
  inherited,
  disabled,
  hideInherit,
}: NoticeConfigFormProps) {
  const { t } = useTranslation('common');
  const set = (patch: Partial<NoticeConfigValues>) => onChange({ ...values, ...patch });

  // Team mode is active when a category baseline is supplied and we're not in
  // category (hideInherit) mode. `hint` is the baseline each field is shown /
  // diffed against: the category baseline in team mode, the flow defaults
  // otherwise.
  const teamMode = !hideInherit && !!inherited;
  const hint = inherited ?? resolved;

  // flow_profile in team mode: show the concrete effective flow; picking the
  // inherited flow clears the override.
  const flowBase = hint?.flow_profile ?? 'regular_team';
  const flowEffective = values.flow_profile ?? flowBase;
  const flowOverridden = values.flow_profile != null && values.flow_profile !== flowBase;

  // currency keeps an explicit "follow the club standard" option even in team
  // mode: unlike the other fields it has no universal default, so its inherited
  // value can be genuinely empty and needs a selectable representation.
  const currencyBase = hint?.currency;
  const currencyOverridden = values.currency != null && values.currency !== currencyBase;

  return (
    <ModeCtx.Provider value={{ hideInherit: !!hideInherit, teamMode }}>
    <div className="flex flex-col gap-5">
      {/* Flow + fees */}
      <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('notices.config.section.flow')}
        </legend>
        <Field
          label={t('notices.config.field.flow_profile')}
          help={t('notices.config.flowHelp')}
          labelExtra={
            teamMode && flowOverridden
              ? <OverrideMark onReset={() => set({ flow_profile: null })} disabled={disabled} />
              : undefined
          }
        >
          <Select
            value={
              teamMode
                ? flowEffective
                : hideInherit
                  ? (values.flow_profile ?? resolved?.flow_profile ?? 'regular_team')
                  : (values.flow_profile ?? INHERIT)
            }
            onValueChange={(v) => {
              if (v === INHERIT) {
                set({ flow_profile: null });
              } else if (teamMode) {
                const picked = v as FlowProfile;
                set({ flow_profile: picked === flowBase ? null : picked });
              } else {
                set({ flow_profile: v as FlowProfile });
              }
            }}
            disabled={!!disabled}
          >
            <SelectTrigger className="min-h-[var(--tap)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!hideInherit && !teamMode && (
                <SelectItem value={INHERIT}>
                  {resolved
                    ? t('notices.config.inheritValue', { value: t(`notices.config.flow.${resolved.flow_profile}`) })
                    : t('notices.config.inherit')}
                </SelectItem>
              )}
              {FLOWS.map((f) => (
                <SelectItem key={f} value={f}>
                  {t(`notices.config.flow.${f}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field
          label={t('notices.config.field.currency')}
          labelExtra={
            teamMode && currencyOverridden
              ? <OverrideMark onReset={() => set({ currency: null })} disabled={disabled} />
              : undefined
          }
        >
          <Select
            value={values.currency ?? INHERIT}
            onValueChange={(v) => {
              if (v === INHERIT) {
                set({ currency: null });
              } else {
                set({ currency: teamMode && v === currencyBase ? null : v });
              }
            }}
            disabled={!!disabled}
          >
            <SelectTrigger className="min-h-[var(--tap)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Currency has no code default; the null option means "follow the
                  club standard". In team mode it names the inherited currency
                  when the category sets one, else "club standard". */}
              <SelectItem value={INHERIT}>
                {hideInherit
                  ? t('notices.config.currencyStandard', { defaultValue: 'Club standard' })
                  : currencyBase
                    ? t('notices.config.followCategory', { value: currencyBase })
                    : t('notices.config.currencyStandard', { defaultValue: 'Club standard' })}
              </SelectItem>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <TriState
          label={t('notices.config.field.requires_membership')}
          value={values.requires_membership}
          onChange={(v) => set({ requires_membership: v })}
          inheritedHint={hint?.requires_membership}
          disabled={!!disabled}
        />
        <NumberField
          label={t('notices.config.field.membership_year')}
          value={values.membership_year}
          onChange={(v) => set({ membership_year: v })}
          min={2000}
          inheritedHint={hint?.membership_year}
          disabled={!!disabled}
        />
      </fieldset>

      {/* Try-out */}
      <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('notices.config.section.tryout')}
        </legend>
        <TriState
          label={t('notices.config.field.tryout_enabled')}
          value={values.tryout_enabled}
          onChange={(v) => set({ tryout_enabled: v })}
          inheritedHint={hint?.tryout_enabled}
          disabled={!!disabled}
        />
        <TriState
          label={t('notices.config.field.tryout_before_payment')}
          value={values.tryout_before_payment}
          onChange={(v) => set({ tryout_before_payment: v })}
          inheritedHint={hint?.tryout_before_payment}
          disabled={!!disabled}
        />
        <NumberField
          label={t('notices.config.field.tryout_length_weeks')}
          value={values.tryout_length_weeks}
          onChange={(v) => set({ tryout_length_weeks: v })}
          min={1}
          inheritedHint={hint?.tryout_length_weeks}
          disabled={!!disabled}
        />
        <NumberField
          label={t('notices.config.field.tryout_length_sessions')}
          value={values.tryout_length_sessions}
          onChange={(v) => set({ tryout_length_sessions: v })}
          min={1}
          inheritedHint={hint?.tryout_length_sessions}
          disabled={!!disabled}
        />
      </fieldset>

      {/* Self-deregistration */}
      <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('notices.config.section.selfDereg')}
        </legend>
        <TriState
          label={t('notices.config.field.self_dereg_enabled')}
          value={values.self_dereg_enabled}
          onChange={(v) => set({ self_dereg_enabled: v })}
          inheritedHint={hint?.self_dereg_enabled}
          disabled={!!disabled}
        />
        <TriState
          label={t('notices.config.field.self_dereg_comment_required')}
          value={values.self_dereg_comment_required}
          onChange={(v) => set({ self_dereg_comment_required: v })}
          inheritedHint={hint?.self_dereg_comment_required}
          disabled={!!disabled}
        />
        <TextField
          label={t('notices.config.field.self_dereg_label')}
          value={values.self_dereg_label}
          onChange={(v) => set({ self_dereg_label: v })}
          inheritedHint={hint?.self_dereg_label}
          disabled={!!disabled}
        />
      </fieldset>

      {/* Booking */}
      <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('notices.config.section.booking')}
        </legend>
        <NumberField
          label={t('notices.config.field.seat_limit')}
          value={values.seat_limit}
          onChange={(v) => set({ seat_limit: v })}
          min={1}
          inheritedHint={hint?.seat_limit}
          disabled={!!disabled}
        />
        <TriState
          label={t('notices.config.field.pay_on_booking')}
          value={values.pay_on_booking}
          onChange={(v) => set({ pay_on_booking: v })}
          inheritedHint={hint?.pay_on_booking}
          disabled={!!disabled}
        />
      </fieldset>

      {/* Reminders + consequence */}
      <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('notices.config.section.reminders')}
        </legend>
        <TriState
          label={t('notices.config.field.reminders_enabled')}
          value={values.reminders_enabled}
          onChange={(v) => set({ reminders_enabled: v })}
          inheritedHint={hint?.reminders_enabled}
          disabled={!!disabled}
        />
        <TextField
          label={t('notices.config.field.consequence')}
          value={values.consequence}
          onChange={(v) => set({ consequence: v })}
          inheritedHint={hint?.consequence}
          disabled={!!disabled}
        />
      </fieldset>
    </div>
    </ModeCtx.Provider>
  );
}
